/**
 * Utilitaires de sanitization pour protéger contre les injections
 * 
 * @description Fonctions pour nettoyer et valider les entrées utilisateur
 * contre les attaques XSS, SQL Injection et Prompt Injection
 * @since 2025-12-26
 */

// ============================================================================
// PROTECTION XSS
// ============================================================================

/**
 * Échappe les caractères HTML dangereux pour prévenir les attaques XSS
 */
export const escapeHtml = (unsafe: string): string => {
  if (!unsafe || typeof unsafe !== 'string') return '';
  
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    .replace(/`/g, '&#x60;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Supprime toutes les balises HTML d'une chaîne
 */
export const stripHtml = (html: string): string => {
  if (!html || typeof html !== 'string') return '';
  
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
};

/**
 * Supprime les balises script et event handlers
 */
export const removeScripts = (input: string): string => {
  if (!input || typeof input !== 'string') return '';
  
  return input
    // Supprimer les balises script
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Supprimer les event handlers (onclick, onerror, etc.)
    .replace(/\bon\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/\bon\w+\s*=\s*[^\s>]*/gi, '')
    // Supprimer javascript: URLs
    .replace(/javascript\s*:/gi, '')
    // Supprimer data: URLs potentiellement dangereux
    .replace(/data\s*:\s*text\/html/gi, '');
};

// ============================================================================
// PROTECTION SQL INJECTION
// ============================================================================

/**
 * Patterns SQL dangereux à détecter
 * ⚠️ ATTENTION: Ces patterns doivent être spécifiques pour éviter les faux positifs
 * Les apostrophes simples (') sont légitimes dans les noms (L'Entreprise, O'Brien, etc.)
 * Les # sont légitimes dans les adresses (#123, Apt #4)
 */
const SQL_INJECTION_PATTERNS = [
  // Commentaires SQL (-- suivi d'espace ou encodé)
  /--\s+/gi,
  /(%2D){2}\s*/gi,
  
  // OR/AND injection avec apostrophes encodées
  /%27\s*(or|and)\s*%27/gi,
  /'\s*or\s*'[^']*'\s*=\s*'/gi,                         // ' or 'x'='x
  /'\s*or\s*1\s*=\s*1/gi,                               // ' or 1=1
  /'\s*or\s*''='/gi,                                    // ' or ''='
  
  // UNION injection
  /'\s*union\s+(all\s+)?select/gi,                      // ' UNION SELECT
  /%27\s*union/gi,
  
  // Commandes dangereuses avec contexte SQL
  /;\s*exec(\s|\+)+(s|x)p\w+/gi,                        // ; exec stored procedure
  /;\s*insert(\s|\+)+into/gi,                           // ; INSERT INTO
  /;\s*delete(\s|\+)+from/gi,                           // ; DELETE FROM
  /;\s*drop(\s|\+)+(table|database)/gi,                 // ; DROP TABLE/DATABASE
  /;\s*update(\s|\+)+\w+(\s|\+)+set/gi,                 // ; UPDATE SET
  /;\s*shutdown/gi,                                     // ; Shutdown command
  
  // Encodage suspect (tentative d'évasion)
  /%00/gi,                                             // Null byte injection
  /0x[0-9a-f]{8,}/gi,                                   // Long hex strings (potential payload)
];

/**
 * Détecte les tentatives d'injection SQL
 * Retourne le pattern trouvé pour le debugging, ou null si aucune injection détectée
 */
export const detectSqlInjection = (input: string): boolean => {
  if (!input || typeof input !== 'string') return false;
  
  for (const pattern of SQL_INJECTION_PATTERNS) {
    // Reset lastIndex pour les regex globales
    pattern.lastIndex = 0;
    if (pattern.test(input)) {
      // Log le pattern qui a matché pour le debugging
      console.warn('🚨 SQL Injection pattern détecté:', pattern.toString());
      // Trouver le texte qui a matché
      pattern.lastIndex = 0;
      const match = pattern.exec(input);
      if (match) {
        console.warn('🚨 Texte suspect:', match[0]);
        // Contexte autour du match
        const index = input.indexOf(match[0]);
        const context = input.substring(Math.max(0, index - 30), Math.min(input.length, index + match[0].length + 30));
        console.warn('🚨 Contexte:', context);
      }
      return true;
    }
  }
  return false;
};

const NUL = String.fromCharCode(0x00);
const SUB = String.fromCharCode(0x1a);

/**
 * Retire les caractères de contrôle invisibles (U+0000-U+0008, U+000B, U+000C,
 * U+000E-U+001F, U+007F) en gardant tabulation et sauts de ligne.
 */
const stripControlChars = (value: string): string =>
  Array.from(value)
    .filter((ch) => {
      const code = ch.charCodeAt(0);
      return !(code <= 0x08 || code === 0x0b || code === 0x0c || (code >= 0x0e && code <= 0x1f) || code === 0x7f);
    })
    .join('');

/**
 * Échappe les caractères spéciaux SQL
 * Note: À utiliser en complément des requêtes paramétrées côté backend
 */
export const escapeSql = (input: string): string => {
  if (!input || typeof input !== 'string') return '';
  
  return input
    .replace(/'/g, "''")
    .replace(/\\/g, '\\\\')
    .split(NUL).join('\\0')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .split(SUB).join('\\Z');
};

// ============================================================================
// PROTECTION PROMPT INJECTION (AI/LLM)
// ============================================================================

/**
 * Patterns de prompt injection courants
 */
const PROMPT_INJECTION_PATTERNS = [
  // Instructions d'override
  /ignore\s+(all\s+)?(previous|above|prior)\s+(instructions?|prompts?|rules?)/gi,
  /disregard\s+(all\s+)?(previous|above|prior)\s+(instructions?|prompts?|rules?)/gi,
  /forget\s+(all\s+)?(previous|above|prior)\s+(instructions?|prompts?|rules?)/gi,
  /override\s+(all\s+)?(instructions?|prompts?|rules?|restrictions?)/gi,
  
  // Jailbreak attempts
  /act\s+as\s+(if\s+)?(you\s+)?(are|were)\s+(an?\s+)?(unrestricted|unfiltered|evil|dangerous)/gi,
  /pretend\s+(you\s+)?(are|were)\s+(an?\s+)?(unrestricted|unfiltered)/gi,
  /you\s+are\s+now\s+(in\s+)?(developer|admin|god|root)\s+mode/gi,
  /enable\s+(developer|admin|god|root|jailbreak)\s+mode/gi,
  /activate\s+(developer|admin|god|root|jailbreak)\s+mode/gi,
  /switch\s+to\s+(developer|admin|unrestricted)\s+mode/gi,
  
  // Role hijacking
  /your\s+new\s+(name|role|persona|character|identity)\s+is/gi,
  /from\s+now\s+on,?\s+(you\s+)?(are|will\s+be)/gi,
  /i\s+want\s+you\s+to\s+act\s+as/gi,
  
  // System prompt extraction
  /reveal\s+(your|the|system)\s+(system\s+)?(prompt|instructions?)/gi,
  /show\s+(me\s+)?(your|the)\s+(system\s+)?(prompt|instructions?)/gi,
  /what\s+(are|is)\s+(your|the)\s+(system\s+)?(prompt|instructions?)/gi,
  /print\s+(your|the)\s+(system\s+)?(prompt|instructions?)/gi,
  /output\s+(your|the)\s+(system\s+)?(prompt|instructions?)/gi,
  
  // Token manipulation
  /<\|.*?\|>/g,           // Special tokens
  /\[\[.*?\]\]/g,         // Bracketed commands
  /\{\{.*?\}\}/g,         // Template injection
  /```system/gi,          // Code block injection
  
  // Encoding bypass attempts
  /base64\s*:\s*[a-zA-Z0-9+/=]+/gi,
  /hex\s*:\s*[0-9a-fA-F]+/gi,
  
  // Delimiter injection
  /---+\s*(system|user|assistant)/gi,
  /###\s*(system|user|assistant|instruction)/gi,
];

/**
 * Détecte les tentatives de prompt injection
 * @returns Un objet avec le résultat de la détection et les patterns trouvés
 */
export const detectPromptInjection = (input: string): { 
  detected: boolean; 
  patterns: string[];
  riskLevel: 'low' | 'medium' | 'high';
} => {
  if (!input || typeof input !== 'string') {
    return { detected: false, patterns: [], riskLevel: 'low' };
  }

  const detectedPatterns: string[] = [];
  
  for (const pattern of PROMPT_INJECTION_PATTERNS) {
    const matches = input.match(pattern);
    if (matches) {
      detectedPatterns.push(...matches);
    }
  }

  const detected = detectedPatterns.length > 0;
  let riskLevel: 'low' | 'medium' | 'high' = 'low';
  
  if (detectedPatterns.length >= 3) {
    riskLevel = 'high';
  } else if (detectedPatterns.length >= 1) {
    riskLevel = 'medium';
  }

  return { detected, patterns: detectedPatterns, riskLevel };
};

/**
 * Sanitise un message destiné à l'IA
 * Supprime ou neutralise les tentatives de prompt injection
 */
export const sanitizePromptInput = (input: string): string => {
  if (!input || typeof input !== 'string') return '';

  let sanitized = input.trim();

  // Supprimer les caractères de contrôle invisibles
  sanitized = stripControlChars(sanitized);
  
  // Supprimer les caractères Unicode spéciaux potentiellement dangereux
  sanitized = sanitized.replace(/[\u200B-\u200F\u2028-\u202F\u2060-\u206F]/g, '');
  
  // Neutraliser les délimiteurs courants utilisés dans les injections
  sanitized = sanitized
    .replace(/---+/g, '—')           // Remplacer les tirets multiples
    .replace(/###/g, '')              // Supprimer les dièses multiples
    .replace(/<\|/g, '<')             // Neutraliser les tokens spéciaux
    .replace(/\|>/g, '>')
    .replace(/```system/gi, '```')    // Neutraliser les blocs système
    .replace(/```user/gi, '```')
    .replace(/```assistant/gi, '```');

  // Limiter la longueur pour éviter les attaques par longueur excessive
  const MAX_PROMPT_LENGTH = 10000;
  if (sanitized.length > MAX_PROMPT_LENGTH) {
    sanitized = sanitized.substring(0, MAX_PROMPT_LENGTH) + '...';
  }

  return sanitized;
};

// ============================================================================
// SANITIZATION GÉNÉRALE DES FORMULAIRES
// ============================================================================

/**
 * Options de sanitization
 */
interface SanitizeOptions {
  stripHtml?: boolean;
  escapeHtml?: boolean;
  trimWhitespace?: boolean;
  maxLength?: number;
  allowedChars?: RegExp;
  toLowerCase?: boolean;
  toUpperCase?: boolean;
}

/**
 * Sanitise une chaîne de caractères avec les options spécifiées
 */
export const sanitizeString = (
  input: string, 
  options: SanitizeOptions = {}
): string => {
  if (!input || typeof input !== 'string') return '';

  let result = input;

  // Supprimer les caractères de contrôle
  result = stripControlChars(result);

  // Options de transformation
  if (options.stripHtml) {
    result = stripHtml(result);
  }

  if (options.escapeHtml) {
    result = escapeHtml(result);
  }

  if (options.trimWhitespace !== false) {
    result = result.trim();
    // Normaliser les espaces multiples
    result = result.replace(/\s+/g, ' ');
  }

  if (options.allowedChars) {
    result = result.replace(new RegExp(`[^${options.allowedChars.source}]`, 'g'), '');
  }

  if (options.maxLength && result.length > options.maxLength) {
    result = result.substring(0, options.maxLength);
  }

  if (options.toLowerCase) {
    result = result.toLowerCase();
  }

  if (options.toUpperCase) {
    result = result.toUpperCase();
  }

  return result;
};

/**
 * Sanitise un objet de formulaire complet
 */
export const sanitizeFormData = <T extends object>(
  formData: T,
  fieldOptions?: Partial<Record<keyof T, SanitizeOptions>>
): T => {
  const sanitized = { ...formData };

  for (const key in sanitized) {
    const value = sanitized[key];
    const options = fieldOptions?.[key] || {};

    if (typeof value === 'string') {
      sanitized[key] = sanitizeString(value, options) as T[typeof key];
    } else if (Array.isArray(value)) {
      sanitized[key] = value.map((item: unknown) => 
        typeof item === 'string' ? sanitizeString(item, options) : item
      ) as T[typeof key];
    }
    // Les objets imbriqués et autres types sont laissés tels quels
  }

  return sanitized;
};

// ============================================================================
// VALIDATEURS DE FORMAT
// ============================================================================

/**
 * Valide et sanitise un email
 */
export const sanitizeEmail = (email: string): string => {
  if (!email || typeof email !== 'string') return '';
  
  const sanitized = email.toLowerCase().trim();
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  
  return emailRegex.test(sanitized) ? sanitized : '';
};

/**
 * Valide et sanitise un numéro de téléphone
 */
export const sanitizePhone = (phone: string): string => {
  if (!phone || typeof phone !== 'string') return '';
  
  // Garde uniquement les chiffres, le + et les espaces
  return phone.replace(/[^\d\s+()-]/g, '').trim();
};

/**
 * Sanitise un nom (prénom, nom de famille, nom d'entreprise)
 */
export const sanitizeName = (name: string): string => {
  if (!name || typeof name !== 'string') return '';
  
  return name
    .trim()
    .replace(/[<>'"`;\\]/g, '')  // Supprimer les caractères dangereux
    .replace(/\s+/g, ' ')         // Normaliser les espaces
    .substring(0, 200);           // Limiter la longueur
};

/**
 * Sanitise un montant numérique
 */
export const sanitizeAmount = (amount: string | number): number => {
  if (typeof amount === 'number') {
    return isNaN(amount) ? 0 : Math.abs(amount);
  }
  
  if (typeof amount === 'string') {
    // Supprimer tout sauf les chiffres et le point décimal
    const cleaned = amount.replace(/[^\d.]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : Math.abs(parsed);
  }
  
  return 0;
};

/**
 * Sanitise une URL
 */
export const sanitizeUrl = (url: string): string => {
  if (!url || typeof url !== 'string') return '';
  
  const trimmed = url.trim();
  
  // Vérifier que c'est une URL http/https valide
  try {
    const parsed = new URL(trimmed);
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return '';
    }
    return parsed.href;
  } catch {
    return '';
  }
};

/**
 * Vérifie si une chaîne est une URL valide (http/https)
 * Utilisé pour valider les champs URL avant envoi au backend
 */
export const isValidUrl = (url: string | null | undefined): boolean => {
  if (!url || typeof url !== 'string') return false;
  return sanitizeUrl(url) !== '';
};

// ============================================================================
// TRANSFORMATION DES DONNÉES POUR LE BACKEND
// ============================================================================

/**
 * Valeurs acceptées par le backend pour chaque enum
 */
export const BACKEND_ENUMS = {
  tailleEntreprise: ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'] as const,
  typeAccompagnement: ['incubation', 'acceleration'] as const,
  niveauMaturiteTechnologique: ['concept_idee', 'preuve_concept', 'prototype', 'demonstrateur', 'produit_commercial'] as const,
  modeleEconomique: ['b2b', 'b2c', 'b2b2c', 'marketplace_plateforme', 'freemium', 'abonnement_saas', 'commission_pourcentage', 'publicite', 'autre'] as const,
};

/**
 * Mapping des valeurs frontend vers backend pour tailleEntreprise
 */
const TAILLE_ENTREPRISE_MAPPING: Record<string, string> = {
  // Valeurs courantes qui pourraient venir du frontend
  '1-10': '1-10',
  '11-50': '11-50',
  '51-200': '51-200',
  '201-500': '201-500',
  '501-1000': '501-1000',
  '1000+': '1000+',
  // Aliases possibles
  'micro': '1-10',
  'petite': '11-50',
  'moyenne': '51-200',
  'grande': '201-500',
  'tres_grande': '501-1000',
  'enterprise': '1000+',
  // Avec espaces
  '1 - 10': '1-10',
  '11 - 50': '11-50',
  '51 - 200': '51-200',
  '201 - 500': '201-500',
  '501 - 1000': '501-1000',
};

/**
 * Mapping des valeurs frontend vers backend pour typeAccompagnement
 */
const TYPE_ACCOMPAGNEMENT_MAPPING: Record<string, string> = {
  'incubation': 'incubation',
  'acceleration': 'acceleration',
  'incubateur': 'incubation',
  'accelerateur': 'acceleration',
  'Incubation': 'incubation',
  'Acceleration': 'acceleration',
  'Accélération': 'acceleration',
};

/**
 * Mapping des valeurs frontend vers backend pour niveauMaturiteTechnologique
 */
const NIVEAU_MATURITE_MAPPING: Record<string, string> = {
  'concept_idee': 'concept_idee',
  'preuve_concept': 'preuve_concept',
  'prototype': 'prototype',
  'demonstrateur': 'demonstrateur',
  'produit_commercial': 'produit_commercial',
  // Aliases possibles
  'concept': 'concept_idee',
  'idee': 'concept_idee',
  'poc': 'preuve_concept',
  'mvp': 'prototype',
  'demo': 'demonstrateur',
  'commercial': 'produit_commercial',
  'produit': 'produit_commercial',
};

/**
 * Mapping des valeurs frontend vers backend pour modeleEconomique
 */
const MODELE_ECONOMIQUE_MAPPING: Record<string, string> = {
  'b2b': 'b2b',
  'b2c': 'b2c',
  'b2b2c': 'b2b2c',
  'marketplace_plateforme': 'marketplace_plateforme',
  'freemium': 'freemium',
  'abonnement_saas': 'abonnement_saas',
  'commission_pourcentage': 'commission_pourcentage',
  'publicite': 'publicite',
  'autre': 'autre',
  // Aliases
  'B2B': 'b2b',
  'B2C': 'b2c',
  'B2B2C': 'b2b2c',
  'marketplace': 'marketplace_plateforme',
  'plateforme': 'marketplace_plateforme',
  'saas': 'abonnement_saas',
  'SaaS': 'abonnement_saas',
  'subscription': 'abonnement_saas',
  'commission': 'commission_pourcentage',
  'ads': 'publicite',
  'advertising': 'publicite',
  'other': 'autre',
};

/**
 * Convertit une date au format ISO 8601 pour le backend
 * Accepte les formats: YYYY-MM-DD, DD/MM/YYYY, timestamps, Date objects
 */
export const toISO8601Date = (date: string | Date | number | null | undefined): string | null => {
  if (!date) return null;
  
  try {
    let dateObj: Date;
    
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'number') {
      dateObj = new Date(date);
    } else if (typeof date === 'string') {
      const trimmed = date.trim();
      
      // Si déjà au format ISO 8601 avec timezone, retourner tel quel
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/.test(trimmed)) {
        return trimmed.endsWith('Z') ? trimmed : trimmed + 'Z';
      }
      
      // Format YYYY-MM-DD (input type="date")
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        dateObj = new Date(trimmed + 'T00:00:00.000Z');
      }
      // Format DD/MM/YYYY
      else if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
        const [day, month, year] = trimmed.split('/');
        dateObj = new Date(`${year}-${month}-${day}T00:00:00.000Z`);
      }
      // Format DD-MM-YYYY
      else if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
        const [day, month, year] = trimmed.split('-');
        dateObj = new Date(`${year}-${month}-${day}T00:00:00.000Z`);
      }
      // Essayer de parser la date directement
      else {
        dateObj = new Date(trimmed);
      }
    } else {
      return null;
    }
    
    // Vérifier que la date est valide
    if (isNaN(dateObj.getTime())) {
      return null;
    }
    
    return dateObj.toISOString();
  } catch {
    return null;
  }
};

/**
 * Normalise la taille d'entreprise pour le backend
 */
export const normalizeTailleEntreprise = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const normalized = TAILLE_ENTREPRISE_MAPPING[value.trim()];
  return normalized || null;
};

/**
 * Normalise le type d'accompagnement pour le backend
 */
export const normalizeTypeAccompagnement = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const normalized = TYPE_ACCOMPAGNEMENT_MAPPING[value.trim()];
  return normalized || null;
};

/**
 * Normalise le niveau de maturité technologique pour le backend
 */
export const normalizeNiveauMaturite = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const normalized = NIVEAU_MATURITE_MAPPING[value.trim()];
  return normalized || null;
};

/**
 * Normalise le modèle économique pour le backend
 */
export const normalizeModeleEconomique = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const normalized = MODELE_ECONOMIQUE_MAPPING[value.trim().toLowerCase()] || 
                     MODELE_ECONOMIQUE_MAPPING[value.trim()];
  return normalized || null;
};

/**
 * Type pour les données d'entreprise à transformer
 */
interface CompanyDataForBackend {
  dateDebutActivites?: string | Date | null;
  tailleEntreprise?: string | null;
  typeAccompagnement?: string | null;
  niveauMaturiteTechnologique?: string | null;
  modeleEconomique?: string | null;
  email?: string | null;
  capitalSocial?: string | number | null;
  [key: string]: unknown;
}

/**
 * Transforme les données d'entreprise pour correspondre aux attentes du backend
 * Utilisé avant l'envoi des formulaires d'entreprise
 */
export const transformCompanyDataForBackend = <T extends object>(data: T): T => {
  // Vue indexable des champs connus : on normalise, convertit et vide des clés.
  const transformed = { ...data } as CompanyDataForBackend;

  // Transformer la date de début d'activités
  if ('dateDebutActivites' in transformed && transformed.dateDebutActivites) {
    const isoDate = toISO8601Date(transformed.dateDebutActivites);
    transformed.dateDebutActivites = isoDate;
  }

  // Normaliser la taille d'entreprise
  if ('tailleEntreprise' in transformed && transformed.tailleEntreprise) {
    transformed.tailleEntreprise = normalizeTailleEntreprise(transformed.tailleEntreprise);
  }

  // Normaliser le type d'accompagnement
  if ('typeAccompagnement' in transformed && transformed.typeAccompagnement) {
    transformed.typeAccompagnement = normalizeTypeAccompagnement(transformed.typeAccompagnement);
  }

  // Normaliser le niveau de maturité technologique
  if ('niveauMaturiteTechnologique' in transformed && transformed.niveauMaturiteTechnologique) {
    transformed.niveauMaturiteTechnologique = normalizeNiveauMaturite(transformed.niveauMaturiteTechnologique);
  }

  // Normaliser le modèle économique
  if ('modeleEconomique' in transformed && transformed.modeleEconomique) {
    transformed.modeleEconomique = normalizeModeleEconomique(transformed.modeleEconomique);
  }

  // Valider et normaliser l'email
  if ('email' in transformed && transformed.email) {
    transformed.email = sanitizeEmail(transformed.email) || null;
  }

  // ✨ VALIDER LES CHAMPS URL (le backend attend des URLs valides avec @IsUrl())
  // Ces champs doivent contenir des URLs Cloudinary ou autres URLs valides
  const urlFields = ['organigramme', 'logo', 'ceoPhoto', 'siteWeb', 'pitch_deck_url', 'demo_video_url'];
  for (const field of urlFields) {
    const raw = transformed[field];
    if (field in transformed && raw) {
      const validUrl = typeof raw === 'string' ? sanitizeUrl(raw) : null;
      transformed[field] = validUrl || null;
    }
  }

  // ✨ CONVERTIR capitalSocial en string (le backend attend un string)
  if ('capitalSocial' in transformed && transformed.capitalSocial !== null && transformed.capitalSocial !== undefined) {
    transformed.capitalSocial = String(transformed.capitalSocial);
  }

  // ✨ ANTI-PERTE DE DONNÉES: Convertir les chaînes vides en null, MAIS conserver les tableaux vides
  // Les tableaux vides [] représentent une intention explicite de "pas de valeurs"
  for (const key in transformed) {
    const value = transformed[key];
    
    // Conserver les tableaux (même vides) - ce sont des données valides
    if (Array.isArray(value)) {
      continue;
    }
    
    // Convertir strings vides et undefined en null
    if (value === '' || value === undefined) {
      transformed[key] = null;
    }
  }

  return transformed as T;
};

/**
 * Valide si une valeur est acceptable pour un enum donné
 */
export const isValidEnumValue = (
  value: string | null | undefined,
  enumType: keyof typeof BACKEND_ENUMS
): boolean => {
  if (!value) return false;
  return (BACKEND_ENUMS[enumType] as readonly string[]).includes(value);
};

// ============================================================================
// EXPORTS PAR DÉFAUT
// ============================================================================

export default {
  // XSS Protection
  escapeHtml,
  stripHtml,
  removeScripts,
  
  // SQL Protection
  detectSqlInjection,
  escapeSql,
  
  // Prompt Injection Protection
  detectPromptInjection,
  sanitizePromptInput,
  
  // General Sanitization
  sanitizeString,
  sanitizeFormData,
  
  // Format Validators
  sanitizeEmail,
  sanitizePhone,
  sanitizeName,
  sanitizeAmount,
  sanitizeUrl,
  
  // Backend Data Transformation
  toISO8601Date,
  normalizeTailleEntreprise,
  normalizeTypeAccompagnement,
  normalizeNiveauMaturite,
  normalizeModeleEconomique,
  transformCompanyDataForBackend,
  isValidEnumValue,
  isValidUrl,
  BACKEND_ENUMS,
};
